import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DynamicFilterIcon, RemoveIcon } from 'foundations-assets';
import { Popover } from 'foundations-components/transitional/components/Popover';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { usePlatformDashboardSelector } from 'platform-dashboard-ui/store';
import { RequestState } from 'platform-dashboard-ui/filter/requests/request-state';
import { selectDataSourcesPropertiesStatus } from 'platform-dashboard-ui/ducks/dataSourcePropertiesSlice';
import GroupedPinnedPropertyFilterTooltip from './GroupedPinnedPropertyFilterTooltip';
import GroupedPinnedPropertyOperatorInput from './GroupedPinnedPropertyOperatorInput';
import PinnedPropertyPopoverButton from '../shared/PinnedPropertyPopoverButton';
import { appliedDashboardFiltersActions } from '../../../../../ducks/applied-dashboard-filters/slice';
import { useQuickFilterGroupNameAndDesc } from '../hooks/useQuickFilterGroupNameAndDesc';
import { track, EVENTS, trackQuickFilterGroupInteraction
// @ts-expect-error migrate upstream dependency
} from '../../../../../../tracking/DashboardUITracker';
import FormattedMessage from 'I18n/components/FormattedMessage';
import QuickFilterErrorBoundaryMessage from '../QuickFilterErrorBoundaryMessage';
import { PinnedPropertyErrorBoundary } from '../panel/PinnedPropertyErrorBoundary';
import { getQuickFilterPropertyDataSource } from '../../../../../lib/dashboardFilters/groupQuickFilterHelpers';
import { selectQuickFilterGroupAppliedOperator } from '../../../../../ducks/quick-filters';
import UIFlex from 'UIComponents/layout/UIFlex';
import { selectHasUnifiedFilterDashboard } from 'dashboard-lib/public/userInfo/gates';
import { GroupedPinnedPropertyOperatorCRMInput } from './GroupedPinnedPropertyOperatorCRMInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GroupedPinnedPropertyFilter = props => {
  const {
    quickFilterGroup,
    dashboardId,
    userHasQuickFilterEditPermission,
    userRole
  } = props;
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const hasUnifiedFilterDashboard = usePlatformDashboardSelector(selectHasUnifiedFilterDashboard);
  const {
    properties,
    systemGenerated,
    name: quickFilterGroupName
  } = quickFilterGroup;
  const dataSourceKeys = properties.map(getQuickFilterPropertyDataSource);
  const dataSourcesPropertiesStatus = usePlatformDashboardSelector(selectDataSourcesPropertiesStatus(dataSourceKeys));
  const {
    name: displayName
  } = useQuickFilterGroupNameAndDesc(quickFilterGroup);
  const handleTogglePopover = () => {
    if (!isOpen) {
      track(EVENTS.openQuickFilterGroupPopover, {
        actionType: 'explore',
        action: 'open-quick-filter-group',
        assetType: 'dashboard',
        quickFilterGroupName,
        role: userRole,
        interactionSource: 'dashboard',
        systemGenerated: !!systemGenerated
      });
    }
    setIsOpen(!isOpen);
  };
  const prevOperator = useSelector(selectQuickFilterGroupAppliedOperator({
    quickFilterGroup,
    dashboardId
  }));
  const handleUnapplyGroupFilter = event => {
    // Prevents toggling the popover.
    event.stopPropagation();
    const payload = {
      dashboardId,
      properties
    };
    dispatch(appliedDashboardFiltersActions.removeGroupDashboardFilter(payload));
    trackQuickFilterGroupInteraction({
      action: 'delete-grouped-filter',
      quickFilterGroupData: {
        quickFilterGroup
      },
      userRole,
      additionalProps: {
        interactionSource: 'quick-filter-popover'
      }
    });
  };
  const isQuickFilterButtonDisabled = quickFilterGroup.properties.length === 0;
  if (dataSourcesPropertiesStatus !== RequestState.SUCCEEDED) {
    return null;
  }
  return /*#__PURE__*/_jsx(Popover, {
    open: isOpen,
    onOpenChange: handleTogglePopover,
    autoPlacement: true,
    placement: 'bottom',
    inlineSize: "400px",
    content: {
      body: /*#__PURE__*/_jsx(PinnedPropertyErrorBoundary, {
        dashboardId: dashboardId,
        messageComponent: QuickFilterErrorBoundaryMessage,
        children: hasUnifiedFilterDashboard ? /*#__PURE__*/_jsx(GroupedPinnedPropertyOperatorCRMInput, {
          quickFilterGroup: quickFilterGroup,
          dashboardId: dashboardId,
          prevOperator: prevOperator,
          userHasQuickFilterEditPermission: userHasQuickFilterEditPermission,
          onApplyGroupFilter: handleTogglePopover,
          userRole: userRole
        }) : /*#__PURE__*/_jsx(GroupedPinnedPropertyOperatorInput, {
          quickFilterGroup: quickFilterGroup,
          dashboardId: dashboardId,
          userHasQuickFilterEditPermission: userHasQuickFilterEditPermission,
          onApplyGroupFilter: handleTogglePopover,
          prevOperator: prevOperator,
          userRole: userRole
        })
      })
    },
    children: /*#__PURE__*/_jsx(PinnedPropertyPopoverButton, {
      dataTestId: `grouped_pinned_property-${quickFilterGroupName.replace(/\s/g, '').toLowerCase()}`,
      isOpen: isOpen,
      disabled: isQuickFilterButtonDisabled,
      onClick: handleTogglePopover,
      prevOperator: prevOperator,
      children: /*#__PURE__*/_jsx(GroupedPinnedPropertyFilterTooltip, {
        disabled: isOpen,
        quickFilterGroup: quickFilterGroup,
        children: /*#__PURE__*/_jsx("span", {
          style: {
            maxWidth: '150px'
          },
          children: /*#__PURE__*/_jsxs(UIFlex, {
            align: "center",
            gap: 3,
            justify: "between",
            children: [/*#__PURE__*/_jsxs(_Fragment, {
              children: [systemGenerated && /*#__PURE__*/_jsx(IconHolder, {
                icon: DynamicFilterIcon,
                size: "200",
                padding: "0"
              }), /*#__PURE__*/_jsx(UITruncateString, {
                useFlex: true,
                fixedChildren: /*#__PURE__*/_jsx(UIIcon, {
                  name: "downCarat",
                  className: "m-left-1"
                }),
                tooltip: '',
                children: displayName
              })]
            }), prevOperator && /*#__PURE__*/_jsx(UITooltip, {
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "propertyList.filterControl.clear"
              }),
              children: /*#__PURE__*/_jsx(IconHolder, {
                marginInlineStart: "200",
                size: "200",
                padding: "0",
                icon: RemoveIcon,
                "data-test-id": `grouped_pinned_property_remove-${quickFilterGroupName.replace(/\s/g, '').toLowerCase()}`,
                onClick: handleUnapplyGroupFilter
              })
            })]
          })
        })
      })
    })
  });
};
export default GroupedPinnedPropertyFilter;